// tslint:disable:indent

import ActionResult from 'app/src/engine/action-results/ActionResult';
import ConveyorTileActionResult, {
	ConveyorTileActionResultType,
} from 'app/src/engine/action-results/ConveyorTileActionResult';
import MoveMovableItemActionResult, {
	MoveMovableItemActionResultApproachingTileFromDirection,
	MoveMovableItemActionResultDirection,
	MoveMovableItemActionResultResult,
	MoveMovableItemActionResultType,
} from 'app/src/engine/action-results/MoveMovableItemActionResult';
import MovableItem, { MovableItemLocation } from 'app/src/engine/movable-items/MovableItem';
import TileInteraction from 'app/src/engine/tile-interactions/TileInteraction';
import ConveyorTile from 'app/src/engine/tiles/ConveyorTile';
import TileWalls from 'app/src/engine/tiles/TileWalls';
import GameStateStore from 'app/src/game/GameStateStore';

type ConveyorTileInteractionInstruction = {
	conveyorActionResult?: ConveyorTileActionResult | null;
	adjacentConveyorActionResult?: ConveyorTileActionResult | null;
	moveMovableItemActionResult?: MoveMovableItemActionResult | null;
};

type ConveyorTileInteractionInstructionTransformationResults = {
	totalConflicts: number;
	transformedInstructions: ConveyorTileInteractionInstruction[];
};

export default class ConveyorTileInteraction extends TileInteraction {
	get priority(): number {
		return 900;
	}

	private _finishedExecuting = false;
	get finishedExecuting(): boolean {
		return this._finishedExecuting;
	}

	public reset(): void {
		this._finishedExecuting = false;
	}

	public _executeSegment(gameStateStore: GameStateStore): ActionResult[] {
		const results: ActionResult[] = [];

		const attemptedInstructions: ConveyorTileInteractionInstruction[] = [];

		// Iterate over each tile.
		for (let row = 0; row < gameStateStore.rows; row++) {
			for (let col = 0; col < gameStateStore.cols; col++) {
				// We only care about ConveyorTiles.
				const tile = gameStateStore.getTile(row, col);
				if (!(tile instanceof ConveyorTile)) {
					continue;
				}

				// Check if movable items exist on this tile.
				let targetMovableItem: MovableItem | null = null;
				for (const movableItem of gameStateStore.movableItems) {
					const movableItemLocation = gameStateStore.movableItemLocations.get(
						movableItem,
					) as MovableItemLocation;
					if (movableItemLocation.row === row && movableItemLocation.col === col) {
						targetMovableItem = movableItem;
					}
				}

				// If there's no movable items on this tile, skip it.
				if (!targetMovableItem) {
					continue;
				}

				// Let's start creating an instruction.
				let instruction: ConveyorTileInteractionInstruction = {};

				// Add this tile to the list of tile actions.
				instruction.conveyorActionResult = new ConveyorTileActionResult(tile, { row, col });
				instruction.adjacentConveyorActionResult = this._getAdjacentConveyorTileActionResult(
					gameStateStore,
					instruction.conveyorActionResult,
				);

				// Get the movement result.
				instruction = this._populateMoveMovableItemActionResult(
					gameStateStore,
					targetMovableItem,
					instruction,
				);

				// Add the instruction to the intended list.
				attemptedInstructions.push(instruction);
			}
		}

		// Resolve movement conflicts. We need to continue doing this until all conflicts are resolved.
		let transformedInstructionSet: ConveyorTileInteractionInstructionTransformationResults;
		do {
			transformedInstructionSet = this._transformConflicts(gameStateStore, attemptedInstructions);
		} while (transformedInstructionSet.totalConflicts !== 0);

		// Flatten the list of conveyor results.
		let allConveyorTileActionResults: ConveyorTileActionResult[] = [];

		for (const instruction of transformedInstructionSet.transformedInstructions) {
			if (instruction.conveyorActionResult) {
				allConveyorTileActionResults.push(instruction.conveyorActionResult);
			}

			if (instruction.adjacentConveyorActionResult) {
				allConveyorTileActionResults.push(instruction.adjacentConveyorActionResult);
			}
		}

		// Prune any duplicates for conveyor results, asking ConveyorTile to resolve conflicts.
		allConveyorTileActionResults = this._pruneDuplicates(
			gameStateStore,
			allConveyorTileActionResults,
		);

		// Add all conveyor results to the final results.
		for (const conveyorTileActionResult of allConveyorTileActionResults) {
			results.push(conveyorTileActionResult);
		}

		// Flatten all the movement results into final results.
		for (const instruction of transformedInstructionSet.transformedInstructions) {
			if (instruction.moveMovableItemActionResult) {
				// The tiles may want to transform the type of movement.
				instruction.moveMovableItemActionResult.askTileToTransform(gameStateStore);

				// Append the movement to the full list of actions.
				results.push(instruction.moveMovableItemActionResult);
			}
		}

		// Execute all final action results.
		for (const actionResult of results) {
			actionResult.execute(gameStateStore);
		}

		// This interaction is finished until it's reset.
		this._finishedExecuting = true;

		return results;
	}

	private _getAdjacentConveyorTileActionResult(
		gameStateStore: GameStateStore,
		conveyorTileActionResult: ConveyorTileActionResult,
	): ConveyorTileActionResult | null {
		// Figure out which direction we're moving.
		const movementDirection = (conveyorTileActionResult.tile as ConveyorTile).getMoveDirection();

		// Generate a new location based on the direction.
		const newLocation = { ...conveyorTileActionResult.location };

		switch (movementDirection) {
			case MoveMovableItemActionResultDirection.Up:
				newLocation.row -= 1;
				break;
			case MoveMovableItemActionResultDirection.Right:
				newLocation.col += 1;
				break;
			case MoveMovableItemActionResultDirection.Down:
				newLocation.row += 1;
				break;
			case MoveMovableItemActionResultDirection.Left:
				newLocation.col -= 1;
				break;
		}

		// Check if we've fallen off the grid.
		if (
			newLocation.row < 0 ||
			newLocation.row >= gameStateStore.rows ||
			newLocation.col < 0 ||
			newLocation.col >= gameStateStore.cols
		) {
			return null;
		}

		// Check if the tile there is a ConveyorTile.
		const adjacentTile = gameStateStore.getTile(newLocation.row, newLocation.col);

		// Create a ConveyorTileActionResult.
		if (adjacentTile instanceof ConveyorTile) {
			return new ConveyorTileActionResult(adjacentTile, newLocation);
		}

		// There's no adjacent conveyor.
		return null;
	}

	private _populateMoveMovableItemActionResult(
		gameStateStore: GameStateStore,
		movableItem: MovableItem,
		instruction: ConveyorTileInteractionInstruction,
	): ConveyorTileInteractionInstruction {
		// Get the item's current location.
		const movableItemLocation = gameStateStore.movableItemLocations.get(
			movableItem,
		) as MovableItemLocation;

		// Create a new copy of the instruction.
		const newInstruction: ConveyorTileInteractionInstruction = {};

		if (instruction.conveyorActionResult) {
			newInstruction.conveyorActionResult = instruction.conveyorActionResult.clone();
		}
		if (instruction.adjacentConveyorActionResult) {
			newInstruction.adjacentConveyorActionResult = instruction.adjacentConveyorActionResult.clone();
		}
		if (instruction.moveMovableItemActionResult) {
			newInstruction.moveMovableItemActionResult = instruction.moveMovableItemActionResult.clone();
		}

		// Satisfy type safety.
		if (!instruction.conveyorActionResult) {
			throw new Error('Invalid instruction');
		}

		// Figure out which direction we're moving.
		const movementDirection = (instruction.conveyorActionResult
			.tile as ConveyorTile).getMoveDirection();

		// Generate a new location based on the direction.
		const newLocation = { ...instruction.conveyorActionResult.location };

		switch (movementDirection) {
			case MoveMovableItemActionResultDirection.Up:
				newLocation.row -= 1;
				break;
			case MoveMovableItemActionResultDirection.Right:
				newLocation.col += 1;
				break;
			case MoveMovableItemActionResultDirection.Down:
				newLocation.row += 1;
				break;
			case MoveMovableItemActionResultDirection.Left:
				newLocation.col -= 1;
				break;
		}

		// Figure out if there's any walls or edges of the map obstructing us.
		if (
			TileWalls.doesWallObstructMovement(
				gameStateStore,
				instruction.conveyorActionResult.location.row,
				instruction.conveyorActionResult.location.col,
				movementDirection,
			)
		) {
			// Movement is obstructed. Make a bounce movement.
			newInstruction.moveMovableItemActionResult = new MoveMovableItemActionResult(
				movableItem,
				movableItemLocation,
			);
			newInstruction.moveMovableItemActionResult.movementType =
				MoveMovableItemActionResultType.Convey;
			newInstruction.moveMovableItemActionResult.movementDirection = movementDirection;
			newInstruction.moveMovableItemActionResult.movementResult =
				MoveMovableItemActionResultResult.Bounce;
		} else {
			// Create a MoveMovableItemActionResult to the new tile.
			newInstruction.moveMovableItemActionResult = new MoveMovableItemActionResult(
				movableItem,
				movableItemLocation,
			);
			newInstruction.moveMovableItemActionResult.newLocation = {
				row: newLocation.row,
				col: newLocation.col,
				orientation: movableItemLocation.orientation,
				turns: movableItemLocation.turns,
			};
			newInstruction.moveMovableItemActionResult.movementType =
				MoveMovableItemActionResultType.Convey;
			newInstruction.moveMovableItemActionResult.movementResult =
				MoveMovableItemActionResultResult.Move;
			newInstruction.moveMovableItemActionResult.movementDirection = movementDirection;

			// Check if the adjacent tile is a ConveyorTile. We know there is a tile there
			// because if there wasn't one (off the grid), doesWallObstructMovement would've
			// returned true.
			const adjacentTile = gameStateStore.getTile(newLocation.row, newLocation.col);

			// Create a ConveyorTileActionResult.
			if (adjacentTile instanceof ConveyorTile) {
				// Figure out how we're approaching this tile.
				let approachingDirection: MoveMovableItemActionResultApproachingTileFromDirection =
					MoveMovableItemActionResultApproachingTileFromDirection.FromBottom;

				switch (movementDirection) {
					case MoveMovableItemActionResultDirection.Up:
						approachingDirection =
							MoveMovableItemActionResultApproachingTileFromDirection.FromBottom;
						break;
					case MoveMovableItemActionResultDirection.Right:
						approachingDirection = MoveMovableItemActionResultApproachingTileFromDirection.FromLeft;
						break;
					case MoveMovableItemActionResultDirection.Down:
						approachingDirection = MoveMovableItemActionResultApproachingTileFromDirection.FromTop;
						break;
					case MoveMovableItemActionResultDirection.Left:
						approachingDirection =
							MoveMovableItemActionResultApproachingTileFromDirection.FromRight;
						break;
				}

				// Figure out how the rotation affects the MovableItem.
				newInstruction.moveMovableItemActionResult.newLocation.turns = adjacentTile.getMovableItemResultTurnsWhenBeingConveyedOntoThisTile(
					movableItemLocation.turns,
					approachingDirection,
				);
				newInstruction.moveMovableItemActionResult.newLocation.orientation = adjacentTile.getMovableItemResultDirectionWhenBeingConveyedOntoThisTile(
					movableItemLocation.orientation,
					approachingDirection,
				);
			}
		}

		// The new instruction is ready. Ship it.
		return newInstruction;
	}

	// Given the current list of instructions, check to see if there are any conflicts. if so,
	// resolve them. Return the transformed list (or just hand back the same list if no
	// transformations were needed) and a count of how many conflicts were resolved.
	private _transformConflicts(
		gameStateStore: GameStateStore,
		instructionsList: ConveyorTileInteractionInstruction[],
	): ConveyorTileInteractionInstructionTransformationResults {
		const results: ConveyorTileInteractionInstructionTransformationResults = {
			totalConflicts: 0,
			transformedInstructions: [],
		};

		// Create an empty grid.
		const grid: ConveyorTileInteractionInstruction[][][] = [];
		for (let row = 0; row < gameStateStore.rows; row++) {
			const gridRow: ConveyorTileInteractionInstruction[][] = [];

			for (let col = 0; col < gameStateStore.cols; col++) {
				gridRow.push([]);
			}

			grid.push(gridRow);
		}

		// Add all instructions to the grid at the location of the movement endpoint.
		for (const instruction of instructionsList) {
			if (!instruction.moveMovableItemActionResult) {
				continue;
			}

			grid[instruction.moveMovableItemActionResult.newLocation.row][
				instruction.moveMovableItemActionResult.newLocation.col
			].push(instruction);
		}

		// Iterate over the tiles looking for conflicts.
		for (let row = 0; row < gameStateStore.rows; row++) {
			for (let col = 0; col < gameStateStore.cols; col++) {
				const instructions = grid[row][col];

				// If there's no instructions, skip ahead.
				if (!instructions.length) {
					continue;
				}

				const tile = gameStateStore.getTile(row, col);

				let instructionsConflict = false;

				// If any tile on the grid has multiple items plotted, it conflicts.
				if (instructions.length > 1 && !tile.allowsMultipleMovableItems) {
					instructionsConflict = true;
				}

				// If this tile isn't a ConveyorTile and there's already a MoveableItem on it,
				// it conflicts.
				if (!(tile instanceof ConveyorTile) && gameStateStore.getMovableItem(row, col)) {
					instructionsConflict = true;
				}

				// If the instructions conflict, convert their movements to to non-movement.
				if (instructionsConflict) {
					results.totalConflicts += 1;

					for (const instruction of instructions) {
						if (instruction.moveMovableItemActionResult) {
							instruction.moveMovableItemActionResult.movementResult =
								MoveMovableItemActionResultResult.Bounce;
							instruction.moveMovableItemActionResult.newLocation =
								instruction.moveMovableItemActionResult.oldLocation;
						}

						if (instruction.conveyorActionResult) {
							instruction.conveyorActionResult.type = ConveyorTileActionResultType.Bounce;
						}

						if (instruction.adjacentConveyorActionResult) {
							instruction.adjacentConveyorActionResult.type = ConveyorTileActionResultType.Bounce;
						}
					}
				}

				// Flatten the list of instructions back into the return value.
				for (const instruction of instructions) {
					results.transformedInstructions.push(instruction);
				}
			}
		}

		return results;
	}

	private _pruneDuplicates(
		gameStateStore: GameStateStore,
		conveyorTileActionResults: ConveyorTileActionResult[],
	): ConveyorTileActionResult[] {
		// Create an empty grid.
		const grid: (ConveyorTileActionResult | null)[][] = [];
		for (let row = 0; row < gameStateStore.rows; row++) {
			const gridRow: (ConveyorTileActionResult | null)[] = [];

			for (let col = 0; col < gameStateStore.cols; col++) {
				gridRow.push(null);
			}

			grid.push(gridRow);
		}

		// Plot all ConveyorTileActionResults on the grid. If a space is already
		// occupied, ask ConveyorTile to resolve the conflict.
		for (const conveyorTileActionResult of conveyorTileActionResults) {
			const oldConveyorTileActionResult =
				grid[conveyorTileActionResult.location.row][conveyorTileActionResult.location.col];

			grid[conveyorTileActionResult.location.row][
				conveyorTileActionResult.location.col
			] = oldConveyorTileActionResult
				? (conveyorTileActionResult.tile as ConveyorTile).resolveConveyorTileActionResultConflict(
						conveyorTileActionResult,
						oldConveyorTileActionResult,
				  )
				: (grid[conveyorTileActionResult.location.row][
						conveyorTileActionResult.location.col
				  ] = conveyorTileActionResult);
		}

		// Flatten the list of results.
		const results: ConveyorTileActionResult[] = [];

		for (let row = 0; row < gameStateStore.rows; row++) {
			for (let col = 0; col < gameStateStore.cols; col++) {
				if (grid[row][col]) {
					results.push(grid[row][col] as ConveyorTileActionResult);
				}
			}
		}

		return results;
	}
}
